import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './RoadmapAgent.css';

function RoadmapAgent() {
    const [loading, setLoading] = useState(false);
    const [activeView, setActiveView] = useState('interactive'); // 'interactive' | 'document'
    const [error, setError] = useState(null);

    // États persistants
    const [roadmap, setRoadmap] = useState(() => {
        const saved = localStorage.getItem('currentRoadmap');
        return saved ? JSON.parse(saved) : null;
    });

    const [markdownContent, setMarkdownContent] = useState(() => {
        return localStorage.getItem('currentMarkdown') || '';
    });

    const [projectData, setProjectData] = useState(() => {
        const saved = localStorage.getItem('projectData');
        return saved ? JSON.parse(saved) : {
            name: '',
            type: 'web-app',
            description: '',
            context: '' // Pour importer de la documentation
        };
    });

    // Sauvegarde automatique
    useEffect(() => {
        if (roadmap) localStorage.setItem('currentRoadmap', JSON.stringify(roadmap));
    }, [roadmap]);

    useEffect(() => {
        if (markdownContent) localStorage.setItem('currentMarkdown', markdownContent);
    }, [markdownContent]);

    useEffect(() => {
        localStorage.setItem('projectData', JSON.stringify(projectData));
    }, [projectData]);

    const webhookUrl = import.meta.env.VITE_N8N_ROADMAP_WEBHOOK_URL ||
        'https://n8n.bessacvps.fr/webhook-test/roadmap-agent';

    const generateRoadmap = async () => {
        if (!projectData.name || !projectData.description) {
            setError('Veuillez remplir au moins le nom et la description');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Construction du prompt enrichi avec le contexte documentaire
            const fullDescription = projectData.description +
                (projectData.context ? `\n\nCONTEXTE DOCUMENTAIRE:\n${projectData.context}` : '');

            const response = await fetch(webhookUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'generate',
                    projectName: projectData.name,
                    projectType: projectData.type,
                    description: fullDescription, // On envoie tout ici
                    timestamp: Date.now()
                }),
            });

            if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

            const data = await response.json();

            if (data.success) {
                setRoadmap(data.roadmap);
                if (data.markdown) {
                    setMarkdownContent(data.markdown);
                    setActiveView('interactive');
                }
            } else {
                setError(data.error || 'Erreur lors de la génération');
            }
        } catch (err) {
            setError(`Erreur réseau : ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const toggleTask = async (phaseIndex, taskIndex) => {
        if (!roadmap) return;
        const updatedRoadmap = { ...roadmap };
        updatedRoadmap.phases[phaseIndex].tasks[taskIndex].completed =
            !updatedRoadmap.phases[phaseIndex].tasks[taskIndex].completed;
        setRoadmap(updatedRoadmap);

        // TODO: Envoyer update au webhook
    };

    return (
        <div className="roadmap-agent">
            <div className="roadmap-header">
                <h1>🗺️ Agent Roadmap IA</h1>
                <p>Importez votre documentation et générez une feuille de route</p>
            </div>

            {!roadmap ? (
                <div className="roadmap-form">
                    <div className="form-group">
                        <label>📝 Nom du Projet</label>
                        <input
                            type="text"
                            value={projectData.name}
                            onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
                            placeholder="Ex: Mon Application SaaS"
                        />
                    </div>

                    <div className="form-group">
                        <label>🏗️ Type de Projet</label>
                        <select
                            value={projectData.type}
                            onChange={(e) => setProjectData({ ...projectData, type: e.target.value })}
                        >
                            <option value="web-app">Application Web</option>
                            <option value="mobile-app">Application Mobile</option>
                            <option value="api">API / Backend</option>
                            <option value="other">Autre</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>📄 Description Courte</label>
                        <textarea
                            value={projectData.description}
                            onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
                            placeholder="Décrivez l'objectif principal..."
                            rows="2"
                        />
                    </div>

                    {/* NOUVEAU PANNEL D'IMPORT DOCUMENTATION */}
                    <div className="form-group context-group">
                        <label>📚 Import Documentation / Contexte (Optionnel)</label>
                        <div className="context-helper">
                            Collez ici vos spécifications, README existant, ou liste de fonctionnalités.
                        </div>
                        <textarea
                            className="context-input"
                            value={projectData.context}
                            onChange={(e) => setProjectData({ ...projectData, context: e.target.value })}
                            placeholder="Collez votre documentation technique ici pour que l'IA l'analyse..."
                            rows="6"
                        />
                    </div>

                    <button
                        className="generate-button"
                        onClick={generateRoadmap}
                        disabled={loading}
                    >
                        {loading ? <span className="spinner"></span> : '🚀 Analyser & Générer'}
                    </button>

                    {error && <div className="error-message">❌ {error}</div>}
                </div>
            ) : (
                <div className="roadmap-display">
                    {/* BARRE D'OUTILS ET VUES */}
                    <div className="roadmap-toolbar">
                        <div className="view-switcher">
                            <button
                                className={activeView === 'interactive' ? 'active' : ''}
                                onClick={() => setActiveView('interactive')}
                            >
                                ✅ Tâches
                            </button>
                            <button
                                className={activeView === 'document' ? 'active' : ''}
                                onClick={() => setActiveView('document')}
                            >
                                📄 Documentation
                            </button>
                        </div>

                        <div className="actions">
                            <button
                                className="action-btn secondary"
                                onClick={() => setRoadmap(null)}
                            >
                                🔄 Nouveau
                            </button>
                        </div>
                    </div>

                    {/* VUE INTERACTIVE */}
                    {activeView === 'interactive' && (
                        <div className="interactive-view">
                            <h2>{roadmap.title}</h2>
                            <p className="roadmap-meta">
                                📅 Généré le {new Date(roadmap.generatedAt).toLocaleDateString()}
                            </p>

                            {roadmap.phases?.map((phase, pIdx) => (
                                <div key={pIdx} className="phase-card">
                                    <h3>{phase.title}</h3>
                                    <p>{phase.description}</p>
                                    <ul>
                                        {phase.tasks?.map((task, tIdx) => (
                                            <li key={tIdx} className={task.completed ? 'completed' : ''}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={task.completed || false}
                                                        onChange={() => toggleTask(pIdx, tIdx)}
                                                    />
                                                    <span>{task.title}</span>
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* VUE DOCUMENTATION MARKDOWN */}
                    {activeView === 'document' && (
                        <div className="document-view markdown-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {markdownContent}
                            </ReactMarkdown>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default RoadmapAgent;
